import { useEffect, useState } from "react";
import {
  Edit,
  Plus,
  SquarePen,
  Trash2,
  X,
  ArrowRightLeft,
  Settings2,
} from "lucide-react";
import { useSelector } from "react-redux";
import userApi from "../../../../api/userApi";
import AddRoleStaffModal from "../AddRoleStaffModal";
import { useNotification } from "../../../../hooks/useNotification";
import AddViewMoreStaffContact from "../AddViewMoreStaffContact";
import TransferPermissionModal from "./TransferPermissionModal";
import AutoAssignSettingsModal from "../../../../features/Setting/components/AutoAssignSettingsModal";

export default function PermissionModal({
  isOpen,
  setIsOpen,
  shop,
  handleManageStaffClick,
}) {
  const { addNotification } = useNotification();
  const auth = useSelector((state) => state.auth);

  const [staffs, setStaffs] = useState([]);

  const [checkedRoles, setCheckedRoles] = useState([]);

  const [isOpenAddRoleStaffModal, setIsOpenAddRoleStaffModal] = useState(false);

  const handleOpenAddRoleStaffModal = (staff) => {
    setIsOpenAddRoleStaffModal(true);
  };

  const [isOpenAddViewMoreStaffModal, setIsOpenAddViewMoreStaffModal] =
    useState(false);

  const [editingStaff, setIsEditingStaff] = useState(null);

  const [isOpenTransferModal, setIsOpenTransferModal] = useState(false);

  const [isOpenAutoAssignModal, setIsOpenAutoAssignModal] = useState(false);

  const [user, setUser] = useState(null);

  const handleDeleteClick = async (staff) => {
    if (
      !window.confirm(`Bạn có muốn gỡ quyền của nhân viên ${staff.name} không?`)
    )
      return;
    const res = await userApi.updateById({
      _id: staff._id,
      shopRoles: staff.shopRoles.filter((s) => s.shopId !== shop._id),
    });
    if (res.status === 1) {
      await getStaffs();
      addNotification("Gỡ quyền nhân viên thành công", res.status);
    } else addNotification(res.message, res.status);
  };

  const handleCloseClick = () => {
    setIsOpen(false);
  };

  const handleChangeRoleAction = async (e, role) => {
    try {
      if (e.target.checked) {
        for (const staff of staffs.filter((s) =>
          s.shopRoles?.find((sr) => sr.shopId === shop._id),
        )) {
          await userApi.updateById({
            _id: staff._id,
            shopRoles: staff.shopRoles.map((sr) =>
              sr.shopId === shop._id
                ? {
                    ...sr,
                    permissions: sr.permissions?.includes(role)
                      ? sr.permissions
                      : [...(sr.permissions ?? []), role],
                  }
                : sr,
            ),
          });
        }
      } else {
        for (const staff of staffs.filter((s) =>
          s.shopRoles?.find((sr) => sr.shopId === shop._id),
        )) {
          await userApi.updateById({
            _id: staff._id,
            shopRoles: staff.shopRoles.map((sr) =>
              sr.shopId === shop._id
                ? {
                    ...sr,
                    permissions: sr.permissions?.includes(role)
                      ? sr.permissions.filter((p) => p !== role)
                      : sr.permissions,
                  }
                : sr,
            ),
          });
        }
      }
      await getUser();
      await getStaffs();
      addNotification("Cập nhật quyền thành công", 1);
    } catch (error) {
      addNotification(error, -1);
    }
  };

  const handleChangePermissions = async (e, staff, permission) => {
    try {
      if (e.target.checked) {
        await userApi.updateById({
          _id: staff._id,
          shopRoles: staff.shopRoles.map((sr) =>
            sr.shopId === shop._id
              ? {
                  ...sr,
                  permissions: sr.permissions?.includes(permission)
                    ? sr.permissions
                    : [...(sr.permissions ?? []), permission],
                }
              : sr,
          ),
        });
      } else {
        await userApi.updateById({
          _id: staff._id,
          shopRoles: staff.shopRoles.map((sr) =>
            sr.shopId === shop._id
              ? {
                  ...sr,
                  permissions: sr.permissions?.includes(permission)
                    ? sr.permissions.filter((p) => p !== permission)
                    : sr.permissions,
                }
              : sr,
          ),
        });
      }
      await getUser();
      await getStaffs();
      addNotification("Cập nhật quyền thành công", 1);
    } catch (error) {
      addNotification(error, -1);
    }
  };

  const getStaffs = async () => {
    const res = await userApi.getByFilter({
      accountId: auth.user.accountId,
      isDelete: false,
    });
    if (res.status === 1) setStaffs(res.data);
  };

  const getUser = async () => {
    const res = await userApi.get(auth.user._id);
    if (res.status === 1) setUser(res.data);
  };

  useEffect(() => {
    if (!auth.user.accountId || !isOpen) return;
    getUser();
    getStaffs();
  }, [auth.user, isOpen]);

  useEffect(() => {
    setCheckedRoles(
      (preRoles) =>
        staffs
          .find((s) => s.shopRoles?.find((sr) => sr.shopId === shop._id))
          ?.shopRoles.find((sr) => sr.shopId === shop._id)
          ?.permissions?.filter(
            (p) =>
              p === "view_only_own_contact" ||
              p === "not_view_contact_no_one_manage",
          ) ?? [...preRoles],
    );
  }, [staffs]);

  return (
    <div>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-16 text-black z-20">
          <div className="bg-white w-full rounded-lg shadow-lg flex flex-col h-[85vh]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">
                Phân quyền Zalo: {shop.name}
              </h2>
              <button
                onClick={handleCloseClick}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="p-4 flex gap-4">
                <button
                  onClick={() => handleOpenAddRoleStaffModal()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-blue-700 transition-colors duration-200"
                >
                  <Plus className="w-4 h-4" />
                  Thêm vào Zalo
                </button>
                <button
                  onClick={handleManageStaffClick}
                  className="bg-green-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-green-700 transition-colors duration-200"
                >
                  <Plus className="w-4 h-4" />
                  Tạo nhân viên mới
                </button>
                <button
                  onClick={() => setIsOpenTransferModal(true)}
                  className="bg-purple-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-purple-700 transition-colors duration-200"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  Chuyển quyền
                </button>
                {/* {auth.user.role?.includes("admin") && (
                  <button
                    onClick={() => setIsOpenAutoAssignModal(true)}
                    className="bg-orange-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-orange-700 transition-colors duration-200"
                  >
                    <Settings2 className="w-4 h-4" />
                    Chia data tự động
                  </button>
                )} */}
              </div>
              <div className="flex-1 px-4 flex flex-col overflow-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300"></th>
                      <th
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300"
                        colSpan={3}
                      >
                        Xem dữ liệu
                      </th>
                      <th
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300"
                        colSpan={2}
                      >
                        Cài đặt
                      </th>
                      <th
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300"
                        colSpan={2}
                      >
                        Phân quyền
                      </th>
                    </tr>
                  </thead>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">
                        STT
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">
                        Nhân viên
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">
                        Hành động
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">
                        Xem thêm dữ liệu của
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">
                        Cài đặt Tag
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">
                        Tin nhắn nhanh
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">
                        Thêm quyền
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">
                        Gỡ quyền
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {staffs
                      .filter(
                        (s) =>
                          s.role !== "super_admin" &&
                          s.shopRoles?.find((sr) => sr.shopId === shop._id),
                      )
                      .map((staff, index) => (
                        <tr
                          key={staff._id}
                          className="hover:bg-gray-50 transition-colors duration-150"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center border border-gray-300">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center border border-gray-300">
                            {staff.name}
                            <br />
                            {staff.shopRoles
                              ?.find((sr) => sr.shopId === shop._id)
                              ?.role?.includes("admin")
                              ? "Quản trị viên"
                              : staff.shopRoles
                                    ?.find((sr) => sr.shopId === shop._id)
                                    ?.role?.includes("editor")
                                ? "Biên tập viên"
                                : ""}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center border border-gray-300">
                            <div className="flex gap-3 justify-center">
                              {staff._id !== auth.user._id && (
                                <button
                                  onClick={() => handleDeleteClick(staff)}
                                  className="flex items-center text-blue-600 hover:text-blue-900 transition-colors duration-200"
                                >
                                  <Trash2 className="w-4 h-4 mr-3" />
                                  Xoá
                                </button>
                              )}
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center border border-gray-300">
                            <div className="flex justify-between">
                              <div></div>
                              <div>
                                {staff.shopRoles.find(
                                  (sr) => sr.shopId === shop._id,
                                )?.role === "admin"
                                  ? "Tất cả"
                                  : staffs
                                      .filter((sf) =>
                                        staff.shopRoles
                                          .find((sr) => sr.shopId === shop._id)
                                          ?.viewMoreStaffIds?.includes(sf._id),
                                      )
                                      .map((sf) => sf.name)
                                      .join(", ")}
                              </div>
                              <div>
                                {staff.shopRoles.find(
                                  (sr) => sr.shopId === shop._id,
                                )?.role === "editor" && (
                                  <button
                                    onClick={() => {
                                      setIsEditingStaff(staff);
                                      setIsOpenAddViewMoreStaffModal(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                                  >
                                    <SquarePen className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center border border-gray-300">
                            <input
                              type="checkbox"
                              checked={staff.shopRoles
                                .find((sr) => sr.shopId === shop._id)
                                ?.permissions?.includes("manage_tag")}
                              onChange={(e) =>
                                handleChangePermissions(e, staff, "manage_tag")
                              }
                              className="w-4 h-4"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center border border-gray-300">
                            <input
                              type="checkbox"
                              checked={staff.shopRoles
                                .find((sr) => sr.shopId === shop._id)
                                ?.permissions?.includes("manage_quick_message")}
                              onChange={(e) =>
                                handleChangePermissions(
                                  e,
                                  staff,
                                  "manage_quick_message",
                                )
                              }
                              className="w-4 h-4"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center border border-gray-300">
                            <input
                              type="checkbox"
                              checked={staff.shopRoles
                                .find((sr) => sr.shopId === shop._id)
                                ?.permissions?.includes("add_manage_contact")}
                              onChange={(e) =>
                                handleChangePermissions(
                                  e,
                                  staff,
                                  "add_manage_contact",
                                )
                              }
                              className="w-4 h-4"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center border border-gray-300">
                            <input
                              type="checkbox"
                              checked={staff.shopRoles
                                .find((sr) => sr.shopId === shop._id)
                                ?.permissions?.includes(
                                  "remove_manage_contact",
                                )}
                              onChange={(e) =>
                                handleChangePermissions(
                                  e,
                                  staff,
                                  "remove_manage_contact",
                                )
                              }
                              className="w-4 h-4"
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="p-4">
                <div className="mt-4 space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedRoles.includes("view_only_own_contact")}
                      onChange={(e) =>
                        handleChangeRoleAction(e, "view_only_own_contact")
                      }
                    />
                    <span>
                      Biên tập viên chỉ nhìn thấy dữ liệu của chính mình quản lý
                    </span>
                  </label>
                </div>
                <div className="mt-2 space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedRoles.includes(
                        "not_view_contact_no_one_manage",
                      )}
                      onChange={(e) =>
                        handleChangeRoleAction(
                          e,
                          "not_view_contact_no_one_manage",
                        )
                      }
                    />
                    <span>
                      Biên tập viên KHÔNG thể nhìn thấy dữ liệu không có ai quản
                      lý (Mặc định là có)
                    </span>
                  </label>
                </div>

                <div className="mt-4 text-sm">
                  <p>
                    <b>
                      <i>
                        Mặc định là quản trị viên sẽ xem được toàn bộ dữ liệu
                      </i>
                    </b>
                  </p>
                  <p>
                    <b>
                      <i>
                        Mặc định là biên tập viên sẽ xem được toàn bộ dữ liệu
                      </i>
                    </b>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <AddRoleStaffModal
            isOpen={isOpenAddRoleStaffModal}
            setIsOpen={setIsOpenAddRoleStaffModal}
            shop={shop}
            staffs={staffs}
            getStaffs={getStaffs}
            checkedRoles={checkedRoles}
          />
          <AddViewMoreStaffContact
            isOpen={isOpenAddViewMoreStaffModal}
            setIsOpen={setIsOpenAddViewMoreStaffModal}
            shop={shop}
            editingStaff={editingStaff}
            staffs={staffs}
            getStaffs={getStaffs}
          />
          <TransferPermissionModal
            isOpen={isOpenTransferModal}
            setIsOpen={setIsOpenTransferModal}
            staffs={staffs}
            onSuccess={getStaffs}
          />
          <AutoAssignSettingsModal
            open={isOpenAutoAssignModal}
            onOpenChange={setIsOpenAutoAssignModal}
            accountId={auth.user.accountId}
            staffs={staffs}
            shop={shop}
          />
        </div>
      )}
    </div>
  );
}
